import React from 'react';
import { Loader2, X, Download, AlertCircle, Upload, Film, CheckCircle } from 'lucide-react';

/**
 * Modal component to show export progress with multiple stages
 * Fixed to not show frame counts since backend uses FFmpeg filter_complex
 */
const ExportProgressModal = ({
  isOpen,
  stage,
  progress,
  message,
  error,
  downloadUrl,
  onClose,
  onDownload,
  onCancel
}) => {
  if (!isOpen) return null;

  // Get stage-specific UI elements
  const getStageInfo = () => {
    switch (stage) {
      case 'preparing':
        return {
          icon: <Loader2 size={48} className="text-ag-green animate-spin" />,
          title: 'Preparing Export',
          description: 'Gathering composition data...'
        };
      case 'initializing':
        return {
          icon: <Loader2 size={48} className="text-ag-green animate-spin" />,
          title: 'Initializing Export',
          description: 'Setting up export job...'
        };
      case 'downloading':
        return {
          icon: <Download size={48} className="text-ag-green animate-pulse" />,
          title: 'Downloading Media',
          description: 'Server is downloading media files...'
        };
      case 'rendering':
        return {
          icon: <Film size={48} className="text-ag-green animate-pulse" />,
          title: 'Rendering Video',
          description: 'FFmpeg is rendering your composition...'
        };
      case 'encoding':
        return {
          icon: <Loader2 size={48} className="text-ag-green animate-spin" />,
          title: 'Encoding Video',
          description: 'Creating final video file...'
        };
      case 'complete':
        return {
          icon: <CheckCircle size={48} className="text-black" />,
          title: 'Export Complete!',
          description: 'Your video is ready to download'
        };
      case 'error':
        return {
          icon: <AlertCircle size={48} className="text-red-400" />,
          title: 'Export Failed',
          description: 'An error occurred during export'
        };
      default:
        return {
          icon: <Loader2 size={48} className="text-ag-green animate-spin" />,
          title: 'Processing',
          description: 'Please wait...'
        };
    }
  };

  const stageInfo = getStageInfo();
  const isProcessing = ['preparing', 'initializing', 'downloading', 'rendering', 'encoding'].includes(stage);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-ag-black-700 border border-ag-black-400 rounded-xl p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">
            {stageInfo.title}
          </h3>
          {!isProcessing && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Content */}
        {isProcessing && (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              {stageInfo.icon}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-400">
                {/* <span>Resolution: {resolution}</span> */}
                <span>{Math.round(progress)}%</span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-ag-black-800 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-ag-green h-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="text-center text-lg font-bold text-white">
                {Math.round(progress)}%
              </div>
            </div>

            {/* Stage-specific message */}
            <p className="text-sm text-gray-400 text-center">
              {message || stageInfo.description}
            </p>

            {/* Stage indicators */}
            <div className="flex items-center justify-center space-x-2 pt-2">
              <StageIndicator active={stage === 'downloading'} complete={['rendering', 'encoding', 'complete'].includes(stage)} label="Media" />
              <div className="w-8 h-0.5 bg-ag-black-400" />
              <StageIndicator active={stage === 'rendering'} complete={['encoding', 'complete'].includes(stage)} label="Render" />
              <div className="w-8 h-0.5 bg-ag-black-400" />
              <StageIndicator active={stage === 'encoding'} complete={stage === 'complete'} label="Encode" />
            </div>

            {/* Cancel button */}
            {onCancel && (
              <button
                onClick={onCancel}
                className="w-full mt-4 bg-ag-black-800 hover:bg-ag-black-600 text-white py-2 px-4 rounded-xl border border-ag-black-400 transition-colors text-sm"
              >
                Cancel Export
              </button>
            )}
          </div>
        )}

        {stage === 'complete' && (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 bg-ag-green rounded-full flex items-center justify-center">
                {stageInfo.icon}
              </div>
            </div>

            {/* <p className="text-center text-gray-300">
              Your video has been exported successfully at {resolution} resolution.
            </p> */}

            <button
              onClick={() => onDownload(downloadUrl)}
              className="w-full bg-ag-green hover:bg-green-500 text-black font-bold py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2"
            >
              <Download size={20} />
              <span>Download Video</span>
            </button>

            {/* {downloadUrl && (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-sm text-ag-green hover:underline"
              >
                Open in new tab
              </a>
            )} */}
          </div>
        )}

        {stage === 'error' && (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                {stageInfo.icon}
              </div>
            </div>

            <p className="text-center text-gray-300">
              An error occurred while exporting your video.
            </p>

            {error && (
              <div className="bg-ag-black-800 border border-red-500 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-sm text-red-400 font-mono">
                  {error}
                </p>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full bg-ag-black-800 hover:bg-ag-black-600 text-white font-bold py-3 px-4 rounded-xl border border-ag-black-400 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage indicator component
const StageIndicator = ({ active, complete, label }) => {
  return (
    <div className="flex flex-col items-center">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${complete ? 'bg-ag-green' : active ? 'bg-ag-green animate-pulse' : 'bg-ag-black-400'
        }`}>
        {complete ? (
          <CheckCircle size={16} className="text-black" />
        ) : (
          <div className={`w-3 h-3 rounded-full ${active ? 'bg-black' : 'bg-ag-black-600'}`} />
        )}
      </div>
      <span className={`text-xs mt-1 ${complete || active ? 'text-white' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
};

export default ExportProgressModal;